import { useEffect, useMemo, useState } from "react";
import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";
import PercentRounded from "@mui/icons-material/PercentRounded";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import ChartCard from "../components/ChartCard";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import { logContabilidadeApiError, obterCarteiraRecebiveis } from "../services/agenteContabilidadeApiService";
import {
  buildEmpresaFilterParams,
  buildEmpreendimentoOptions,
  findEmpreendimentoByFilterValue,
  isTodosEmpreendimentos,
  normalizeEmpreendimentoContext,
  TODOS_EMPREENDIMENTOS_VALUE,
} from "../utils/empreendimentoSelector";
import {
  consolidateTimelineByMonth,
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import { colors } from "../constants/designTokens";
import { formatCurrency, formatPercent } from "../utils/formatters";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function normalizeCarteiraItem(item) {
  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    idClienteEmpresa: getFirstValue(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa"], ""),
    pctVendido: toNumber(getFirstValue(item, ["pctVendido", "percentualVendido", "percentual_vendido"])),
    poc: toNumber(getFirstValue(item, ["poc", "pocExecucao", "poc_execucao"])),
    vendasContratadas: toNumber(getFirstValue(item, ["vendasContratadas", "vendas_contratadas", "vgvContratado", "vgv"])),
    carteiraRecebiveis: toNumber(getFirstValue(item, ["carteiraRecebiveis", "carteira_recebiveis", "contasReceberFinanceiro"])),
    recebimentosAcumulados: toNumber(getFirstValue(item, ["recebimentosAcumulados", "recebimentos_acumulados", "recebimentoAcumulado"])),
    recebimentoMensal: toNumber(getFirstValue(item, ["recebimentoMensal", "recebimento_mensal"])),
    receitaApropriada: toNumber(getFirstValue(item, ["receitaApropriada", "receita_apropriada"])),
    contasReceberContabil: toNumber(getFirstValue(item, ["contasReceberContabil", "contas_receber_contabil", "contasReceberAdtoCliente"])),
  };
}

function normalizeCarteiraResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeCarteiraItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(
      payload.empreendimentosIntersecao ||
        payload.empreendimentos_intersecao ||
        payload.intersecaoEmpreendimentos ||
        payload.intersecao_empreendimentos
    ),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

export default function CarteiraRecebiveisPage() {
  const { state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("carteira-recebiveis");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiCarteiraData, setApiCarteiraData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpreendimentos),
    [backendEmpreendimentos]
  );
  const [selectedBackendEmpresa, setSelectedBackendEmpresa] = usePageEmpresaSelection("carteira-recebiveis", backendEmpresaOptions);

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchCarteira() {
      if (!backendCatalogSyncKey && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      if (hasBackendEmpreendimentos && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const params = buildEmpresaFilterParams(selectedBackendEmpresa);
        const payload = await obterCarteiraRecebiveis(params);
        const normalizedData = normalizeCarteiraResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiCarteiraData(normalizedData);
          setApiStatus("success");
        } else {
          setApiCarteiraData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("carteira-recebiveis", error);
        setApiStatus("error");
      }
    }

    fetchCarteira();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, hasBackendEmpreendimentos, selectedBackendEmpresa]);

  const usingApiData = Boolean(apiCarteiraData?.timeline?.length);
  const carteiraData = usingApiData ? apiCarteiraData : { months: [], timeline: [] };
  const { timeline } = carteiraData;
  const isTodos = isTodosEmpreendimentos(selectedBackendEmpresa);
  const visibleTimeline = useMemo(
    () => (isTodos ? consolidateTimelineByMonth(timeline, ["pctVendido", "poc"]) : timeline),
    [isTodos, timeline]
  );
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : carteiraData.months;

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const currentSnapshot = visibleTimeline.find((item) => item.mes_ano === activeMonth) || visibleTimeline[visibleTimeline.length - 1];
  const intersecaoEmpreendimentos = getIntersecaoForMonth(carteiraData, activeMonth);
  const selectedEmpreendimentoContext = isTodos
    ? { filtro: TODOS_EMPREENDIMENTOS_VALUE, nomeEmpresa: "Todos Empreendimentos" }
    : normalizeEmpreendimentoContext(findEmpreendimentoByFilterValue(backendEmpreendimentos, selectedBackendEmpresa));

  const empreendimentoOptions = backendEmpresaOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpresa;

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpresa(value || "");
    setApiAvailableMonths([]);
  }

  const metrics = currentSnapshot
    ? [
        { label: "% Vendido", value: formatPercent(currentSnapshot.pctVendido, { decimals: 2 }), variant: "purple", icon: PercentRounded },
        { label: "% POC - Percentage of Completion", value: formatPercent(currentSnapshot.poc, { decimals: 2 }), variant: "blue", icon: TrendingUpRounded },
        { label: "Vendas Contratadas (VGV)", value: formatCurrency(currentSnapshot.vendasContratadas), variant: "royal", icon: TrendingUpRounded },
        { label: "Contas a Receber (Financeiro)", value: formatCurrency(currentSnapshot.carteiraRecebiveis), variant: "magenta", icon: AccountBalanceWalletOutlined },
        { label: "Recebimentos Acumulados", value: formatCurrency(currentSnapshot.recebimentosAcumulados), variant: "teal", icon: AccountBalanceWalletOutlined },
        { label: "Recebimento do Mês", value: formatCurrency(currentSnapshot.recebimentoMensal), variant: "orange", icon: CreditCardOutlined },
        { label: "Receita Apropriada", value: formatCurrency(currentSnapshot.receitaApropriada), variant: "green", icon: ReceiptLongOutlined },
        { label: "Contas a Receber (Contabil)", value: formatCurrency(Math.abs(currentSnapshot.contasReceberContabil)), variant: "red", icon: AccountBalanceWalletOutlined },
      ]
    : [];

  const chartData = visibleTimeline.map((item) => ({
    mes_ano: item.mes_ano,
    vendasContratadas: item.vendasContratadas / 1000000,
    recebimentosAcumulados: item.recebimentosAcumulados / 1000000,
    carteiraRecebiveis: item.carteiraRecebiveis / 1000000,
  }));
  const isLoadingCarteira = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;

  return (
    <AgenteContabilidadeShell
      title="Carteira de Recebíveis"
      subtitle="Análise mensal da carteira, recebimentos e evolução"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "carteira-recebiveis",
        empresa: selectedBackendEmpresa,
        competencia: activeMonth,
        dadosVisiveis: currentSnapshot
          ? {
              percentualVendido: currentSnapshot.pctVendido,
              poc: currentSnapshot.poc,
              vendasContratadas: currentSnapshot.vendasContratadas,
              carteiraRecebiveis: currentSnapshot.carteiraRecebiveis,
              recebimentosAcumulados: currentSnapshot.recebimentosAcumulados,
              recebimentoMensal: currentSnapshot.recebimentoMensal,
              receitaApropriada: currentSnapshot.receitaApropriada,
              contasReceberContabil: currentSnapshot.contasReceberContabil,
              empresaSelecionada: selectedEmpreendimentoContext,
            }
          : {},
      }}
      actions={[
        <FilterSelect
          key="emp"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={286}
        />,
      ]}
    >
      {isLoadingCarteira ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados de carteira
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos os indicadores calculados pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !visibleTimeline.length ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
              Nenhum dado disponível
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
              Não encontramos dados disponíveis para exibição no momento.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
          <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
          <EmpreendimentosIntersecaoNotice
            isTodosEmpreendimentos={isTodos}
            activeMonth={activeMonth}
            empreendimentos={intersecaoEmpreendimentos}
          />
          <KpiGrid metrics={metrics} />
          <ChartCard
            title="Evolução da Carteira"
            subtitle={`Mês de referência: ${activeMonth}`}
            data={chartData}
            series={[
              { key: "vendasContratadas", name: "Vendas Contratadas", color: "#10b981" },
              { key: "recebimentosAcumulados", name: "Recebimentos Acum.", color: "#6366f1" },
              { key: "carteiraRecebiveis", name: "Carteira", color: "#f59e0b" },
            ]}
            headerGradient="purple"
          />
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
